import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useMemo, useState } from "react";
import ReactAnimateHeight from "react-animate-height";
import styles from "./FundraiserWidget.module.scss";
import type { SanityImageObject } from "@sanity/image-url";
import { FetchFundraiserResult } from "../../../../studio/sanity.types";
import { NavLink } from "../../../shared/components/Navbar/Navbar";
import { validateWidgetConfig } from "./validateWidgetConfig";
import { useFundraiserForm } from "./hooks/useFundraiserForm";
import { usePaneTransition } from "./hooks/usePaneTransition";
import { useRegisterDonation } from "./hooks/useRegisterDonation";
import { OrganizationInfoPane } from "./panes/OrganizationInfoPane";
import { DonationDetailsPane } from "./panes/DonationDetailsPane";
import { PaymentMethodPane } from "./panes/PaymentMethodPane";
import { BankTransferPane } from "./panes/BankTransferPane";
import { FormattingLocale } from "../../../../util/formatting";
import { supportsDonationFrequency } from "./paymentMethodAvailability";
import { RecurringDonation } from "../../../shared/components/Widget/types/Enums";
type WidgetConfig = NonNullable<NonNullable<FetchFundraiserResult["page"]>["fundraiser_widget_configuration"]>;
interface DonationWidgetProps {
  widgetConfig: WidgetConfig | null;
  suggestedAmounts?: number[] | null;
  monthlyDonations?: {
    enabled?: boolean;
    single_donation_text?: string;
    monthly_donation_text?: string;
  } | null;
  fundraiserId: string;
  organizationInfo: {
    organizationPageSlug: string;
    textTemplate: string;
    organization: NonNullable<FetchFundraiserResult["page"]>["fundraiser_organization"];
    databaseIds: {
      causeAreaId: number;
      organizationId: number;
    };
  };
  locale: FormattingLocale;
}
export const FundraiserWidget: React.FC<DonationWidgetProps> = t0 => {
  const $ = _c(138);
  const {
    widgetConfig,
    suggestedAmounts,
    monthlyDonations,
    fundraiserId,
    organizationInfo,
    locale
  } = t0;
  let t1;
  if ($[0] !== widgetConfig?.payment_methods) {
    t1 = widgetConfig?.payment_methods?.map(_temp) || [];
    $[0] = widgetConfig?.payment_methods;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    formData,
    updateField
  } = useFundraiserForm(t1, widgetConfig?.tax_deduction_enabled ? widgetConfig?.tax_deduction?.minimum_amount : undefined);
  const {
    step,
    paneRefs,
    currentHeight,
    getPaneClassName,
    isPaneVisible,
    goToNextStep,
    goToPreviousStep
  } = usePaneTransition(4);
  const [organizationInfoPaneRef, donationDetailsPaneRef, paymentMethodPaneRef, bankTransferPaneRef] = paneRefs;
  let t2;
  if ($[2] !== widgetConfig?.payment_methods) {
    t2 = widgetConfig?.payment_methods || [];
    $[2] = widgetConfig?.payment_methods;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== goToNextStep) {
    t3 = () => goToNextStep();
    $[4] = goToNextStep;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== fundraiserId || $[7] !== locale || $[8] !== organizationInfo || $[9] !== t2 || $[10] !== t3) {
    t4 = {
      fundraiserId,
      organizationInfo,
      paymentMethods: t2,
      onSuccess: t3,
      locale
    };
    $[6] = fundraiserId;
    $[7] = locale;
    $[8] = organizationInfo;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const {
    loading,
    error: registrationError,
    kid,
    registerDonation
  } = useRegisterDonation(t4);
  const [privacyPolicyError, setPrivacyPolicyError] = useState(false);
  widgetConfig?.payment_methods;
  let t5;
  if ($[12] !== formData.recurring || $[13] !== locale || $[14] !== widgetConfig?.payment_methods) {
    t5 = widgetConfig?.payment_methods?.filter(method_0 => supportsDonationFrequency(method_0._type, formData.recurring, locale)) || [];
    $[12] = formData.recurring;
    $[13] = locale;
    $[14] = widgetConfig?.payment_methods;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const availablePaymentMethods = t5;
  let t6;
  let t7;
  if ($[16] !== availablePaymentMethods || $[17] !== formData.paymentMethod || $[18] !== updateField) {
    t6 = () => {
      if (availablePaymentMethods.length > 0 && !availablePaymentMethods.some(method_1 => method_1._type === formData.paymentMethod)) {
        updateField("paymentMethod", availablePaymentMethods[0]._type);
      }
    };
    t7 = [availablePaymentMethods, formData.paymentMethod, updateField];
    $[16] = availablePaymentMethods;
    $[17] = formData.paymentMethod;
    $[18] = updateField;
    $[19] = t6;
    $[20] = t7;
  } else {
    t6 = $[19];
    t7 = $[20];
  }
  useEffect(t6, t7);
  let config;
  let t8;
  let t9;
  if ($[21] !== locale || $[22] !== organizationInfo.organization || $[23] !== widgetConfig) {
    t9 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const validation = validateWidgetConfig(widgetConfig, organizationInfo.organization);
      if (!validation.valid) {
        t9 = validation.error;
        break bb0;
      }
      config = validation.config;
      t8 = config.payment_methods.some(method_2 => supportsDonationFrequency(method_2._type, RecurringDonation.RECURRING, locale));
    }
    $[21] = locale;
    $[22] = organizationInfo.organization;
    $[23] = widgetConfig;
    $[24] = config;
    $[25] = t8;
    $[26] = t9;
  } else {
    config = $[24];
    t8 = $[25];
    t9 = $[26];
  }
  if (t9 !== Symbol.for("react.early_return_sentinel")) {
    return t9;
  }
  const hasMonthlyPaymentMethod = t8;
  let t10;
  if ($[27] !== monthlyDonations) {
    t10 = monthlyDonations?.enabled ? {
      single_donation_text: monthlyDonations.single_donation_text,
      monthly_donation_text: monthlyDonations.monthly_donation_text
    } : undefined;
    $[27] = monthlyDonations;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  const monthlyDonationConfig = t10;
  if (monthlyDonationConfig && !hasMonthlyPaymentMethod) {
    return "No configured payment method supports monthly donations in the fundraiser widget";
  }
  if (monthlyDonationConfig && (!monthlyDonationConfig.single_donation_text || !monthlyDonationConfig.monthly_donation_text)) {
    return "Missing monthly donation labels for fundraiser widget";
  }
  if (availablePaymentMethods.length === 0) {
    return "No payment method supports the selected donation frequency";
  }
  let t11;
  if ($[29] !== suggestedAmounts) {
    t11 = Array.isArray(suggestedAmounts) ? suggestedAmounts.filter(_temp2) : [];
    $[29] = suggestedAmounts;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const normalizedSuggestedAmounts = t11;
  let t12;
  if ($[31] !== config.privacy_policy.require_checkbox || $[32] !== formData || $[33] !== registerDonation) {
    t12 = () => {
      if (config.privacy_policy?.require_checkbox && !formData.privacyPolicyAccepted) {
        setPrivacyPolicyError(true);
        return;
      }
      setPrivacyPolicyError(false);
      registerDonation(formData);
    };
    $[31] = config.privacy_policy.require_checkbox;
    $[32] = formData;
    $[33] = registerDonation;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  const handlePaymentMethodSubmit = t12;
  const t13 = `translateX(${step === 0 ? "-0.5rem" : "0"})`;
  const t14 = step === 0 ? 0 : 1;
  let t15;
  if ($[35] !== t13 || $[36] !== t14) {
    t15 = {
      transform: t13,
      opacity: t14
    };
    $[35] = t13;
    $[36] = t14;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== goToPreviousStep || $[39] !== t15) {
    t16 = <button className={styles["donation-widget__back-button"]} onClick={goToPreviousStep} aria-label="Go back" data-cy="fundraiser-back-button" style={t15}>←</button>;
    $[38] = goToPreviousStep;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  const t17 = typeof currentHeight === "number" ? currentHeight : "auto";
  let t18;
  if ($[41] !== getPaneClassName) {
    t18 = getPaneClassName(0);
    $[41] = getPaneClassName;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== isPaneVisible) {
    t19 = isPaneVisible(0);
    $[43] = isPaneVisible;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  const t20 = organizationInfo.organization.logo as SanityImageObject;
  let t21;
  if ($[45] !== organizationInfo.organization.name || $[46] !== organizationInfo.organizationPageSlug || $[47] !== organizationInfo.textTemplate || $[48] !== t20) {
    t21 = {
      name: organizationInfo.organization.name,
      logo: t20,
      textTemplate: organizationInfo.textTemplate,
      organizationSlug: organizationInfo.organizationPageSlug
    };
    $[45] = organizationInfo.organization.name;
    $[46] = organizationInfo.organizationPageSlug;
    $[47] = organizationInfo.textTemplate;
    $[48] = t20;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== config.header || $[51] !== goToNextStep || $[52] !== organizationInfoPaneRef || $[53] !== t18 || $[54] !== t19 || $[55] !== t21) {
    t22 = <OrganizationInfoPane ref={organizationInfoPaneRef} className={t18} visible={t19} organizationInfo={t21} buttonText={config.header} onNext={goToNextStep} />;
    $[50] = config.header;
    $[51] = goToNextStep;
    $[52] = organizationInfoPaneRef;
    $[53] = t18;
    $[54] = t19;
    $[55] = t21;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  let t23;
  if ($[57] !== getPaneClassName) {
    t23 = getPaneClassName(1);
    $[57] = getPaneClassName;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  let t24;
  if ($[59] !== isPaneVisible) {
    t24 = isPaneVisible(1);
    $[59] = isPaneVisible;
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  const t25 = monthlyDonationConfig as {
    single_donation_text: string;
    monthly_donation_text: string;
  } | undefined;
  let t26;
  if ($[61] !== config.currency_symbol || $[62] !== config.donation_amount_label || $[63] !== config.message_label || $[64] !== config.name_label || $[65] !== config.next_button_text || $[66] !== config.show_name_label || $[67] !== normalizedSuggestedAmounts || $[68] !== t25) {
    t26 = {
      suggested_amounts: normalizedSuggestedAmounts,
      currency_symbol: config.currency_symbol,
      donation_amount_label: config.donation_amount_label,
      name_label: config.name_label,
      message_label: config.message_label,
      show_name_label: config.show_name_label,
      next_button_text: config.next_button_text,
      monthly_donations: t25
    };
    $[61] = config.currency_symbol;
    $[62] = config.donation_amount_label;
    $[63] = config.message_label;
    $[64] = config.name_label;
    $[65] = config.next_button_text;
    $[66] = config.show_name_label;
    $[67] = normalizedSuggestedAmounts;
    $[68] = t25;
    $[69] = t26;
  } else {
    t26 = $[69];
  }
  let t27;
  if ($[70] !== donationDetailsPaneRef || $[71] !== formData || $[72] !== goToNextStep || $[73] !== locale || $[74] !== t23 || $[75] !== t24 || $[76] !== t26 || $[77] !== updateField) {
    t27 = <DonationDetailsPane ref={donationDetailsPaneRef} className={t23} visible={t24} formData={formData} onChange={updateField} onSubmit={goToNextStep} config={t26} locale={locale} />;
    $[70] = donationDetailsPaneRef;
    $[71] = formData;
    $[72] = goToNextStep;
    $[73] = locale;
    $[74] = t23;
    $[75] = t24;
    $[76] = t26;
    $[77] = updateField;
    $[78] = t27;
  } else {
    t27 = $[78];
  }
  let t28;
  if ($[79] !== getPaneClassName) {
    t28 = getPaneClassName(2);
    $[79] = getPaneClassName;
    $[80] = t28;
  } else {
    t28 = $[80];
  }
  let t29;
  if ($[81] !== isPaneVisible) {
    t29 = isPaneVisible(2);
    $[81] = isPaneVisible;
    $[82] = t29;
  } else {
    t29 = $[82];
  }
  let t30;
  if ($[83] !== config.tax_deduction || $[84] !== config.tax_deduction_enabled) {
    t30 = config.tax_deduction_enabled && config.tax_deduction ? {
      label: config.tax_deduction.label,
      tooltip_text: config.tax_deduction.tooltip_text,
      ssn_label: config.tax_deduction.ssn_label,
      ssn_invalid_error_text: config.tax_deduction.ssn_invalid_error_text,
      ssn_suspicious_error_text: config.tax_deduction.ssn_suspicious_error_text
    } : undefined;
    $[83] = config.tax_deduction;
    $[84] = config.tax_deduction_enabled;
    $[85] = t30;
  } else {
    t30 = $[85];
  }
  let t31;
  if ($[86] !== config.newsletter || $[87] !== config.newsletter_enabled) {
    t31 = config.newsletter_enabled && config.newsletter ? {
      label: config.newsletter.label
    } : undefined;
    $[86] = config.newsletter;
    $[87] = config.newsletter_enabled;
    $[88] = t31;
  } else {
    t31 = $[88];
  }
  const t32 = config.privacy_policy.privacy_policy_url as NavLink;
  let t33;
  if ($[89] !== config.privacy_policy.require_checkbox || $[90] !== config.privacy_policy.required_error_text || $[91] !== config.privacy_policy.text || $[92] !== t32) {
    t33 = {
      text: config.privacy_policy.text,
      require_checkbox: config.privacy_policy.require_checkbox,
      required_error_text: config.privacy_policy.required_error_text,
      privacy_policy_url: t32
    };
    $[89] = config.privacy_policy.require_checkbox;
    $[90] = config.privacy_policy.required_error_text;
    $[91] = config.privacy_policy.text;
    $[92] = t32;
    $[93] = t33;
  } else {
    t33 = $[93];
  }
  let t34;
  if ($[94] !== availablePaymentMethods || $[95] !== config.allow_anonymous_donations || $[96] !== config.email_label || $[97] !== t30 || $[98] !== t31 || $[99] !== t33) {
    t34 = {
      tax_deduction: t30,
      newsletter: t31,
      email_label: config.email_label,
      privacy_policy: t33,
      payment_methods: availablePaymentMethods,
      allow_anonymous_donations: config.allow_anonymous_donations
    };
    $[94] = availablePaymentMethods;
    $[95] = config.allow_anonymous_donations;
    $[96] = config.email_label;
    $[97] = t30;
    $[98] = t31;
    $[99] = t33;
    $[100] = t34;
  } else {
    t34 = $[100];
  }
  let t35;
  if ($[101] !== formData || $[102] !== handlePaymentMethodSubmit || $[103] !== loading || $[104] !== locale || $[105] !== paymentMethodPaneRef || $[106] !== privacyPolicyError || $[107] !== registrationError || $[108] !== t28 || $[109] !== t29 || $[110] !== t34 || $[111] !== updateField) {
    t35 = <PaymentMethodPane ref={paymentMethodPaneRef} className={t28} visible={t29} formData={formData} onChange={updateField} onSubmit={handlePaymentMethodSubmit} loading={loading} error={registrationError} config={t34} privacyPolicyError={privacyPolicyError} locale={locale} />;
    $[101] = formData;
    $[102] = handlePaymentMethodSubmit;
    $[103] = loading;
    $[104] = locale;
    $[105] = paymentMethodPaneRef;
    $[106] = privacyPolicyError;
    $[107] = registrationError;
    $[108] = t28;
    $[109] = t29;
    $[110] = t34;
    $[111] = updateField;
    $[112] = t35;
  } else {
    t35 = $[112];
  }
  let t36;
  if ($[113] !== getPaneClassName) {
    t36 = getPaneClassName(3);
    $[113] = getPaneClassName;
    $[114] = t36;
  } else {
    t36 = $[114];
  }
  let t37;
  if ($[115] !== isPaneVisible) {
    t37 = isPaneVisible(3);
    $[115] = isPaneVisible;
    $[116] = t37;
  } else {
    t37 = $[116];
  }
  const t38 = formData.paymentMethod === "bank" || formData.paymentMethod === "dkbank";
  let t39;
  if ($[117] !== config.bank_account_details) {
    t39 = config.bank_account_details ? {
      account_number_prefix: config.bank_account_details.account_number_prefix,
      account_number: config.bank_account_details.account_number,
      kid_prefix: config.bank_account_details.kid_prefix,
      transfer_delay_text: config.bank_account_details.transfer_delay_text,
      account_owner_text: config.bank_account_details.account_owner_text,
      bank_transfer_info: config.bank_account_details.bank_transfer_info
    } : undefined;
    $[117] = config.bank_account_details;
    $[118] = t39;
  } else {
    t39 = $[118];
  }
  let t40;
  if ($[119] !== bankTransferPaneRef || $[120] !== kid || $[121] !== t36 || $[122] !== t37 || $[123] !== t38 || $[124] !== t39) {
    t40 = <BankTransferPane ref={bankTransferPaneRef} className={t36} visible={t37} kid={kid} showBankInfo={t38} bankDetails={t39} />;
    $[119] = bankTransferPaneRef;
    $[120] = kid;
    $[121] = t36;
    $[122] = t37;
    $[123] = t38;
    $[124] = t39;
    $[125] = t40;
  } else {
    t40 = $[125];
  }
  let t41;
  if ($[126] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <div className={styles["donation-widget__bottom_shade"]} />;
    $[126] = t41;
  } else {
    t41 = $[126];
  }
  let t42;
  if ($[127] !== t22 || $[128] !== t27 || $[129] !== t35 || $[130] !== t40) {
    t42 = <div className={styles["donation-widget__inner"]}>{t22}{t27}{t35}{t40}{t41}</div>;
    $[127] = t22;
    $[128] = t27;
    $[129] = t35;
    $[130] = t40;
    $[131] = t42;
  } else {
    t42 = $[131];
  }
  let t43;
  if ($[132] !== t17 || $[133] !== t42) {
    t43 = <ReactAnimateHeight duration={300} height={t17} className={styles["donation-widget__panes"]}>{t42}</ReactAnimateHeight>;
    $[132] = t17;
    $[133] = t42;
    $[134] = t43;
  } else {
    t43 = $[134];
  }
  let t44;
  if ($[135] !== t16 || $[136] !== t43) {
    t44 = <div className={styles["donation-widget"]} data-cy="fundraiser-widget">{t16}{t43}</div>;
    $[135] = t16;
    $[136] = t43;
    $[137] = t44;
  } else {
    t44 = $[137];
  }
  return t44;
};
function _temp(method) {
  return method._type;
}
function _temp2(amount) {
  return typeof amount === "number" && amount > 0;
}